import MapIcon from '@mui/icons-material/Map';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import MapComponent from '/@/components/map';
import { _ } from '/@/locale';
import FieldItem from './field-item';
import FieldSelectMapModal from './field-select-map-modal';
import type { FieldSelectState } from './use-field-select';

type FieldSelectListAndMapProps = Pick<
    FieldSelectState,
    | 'fieldList'
    | 'field'
    | 'setField'
    | 'selectedProduct'
    | 'product'
    | 'showGrowersFields'
    | 'fieldRefs'
    | 'mapRef'
    | 'handleFieldClick'
    | 'fieldsQuery'
    | 'lastCampaigns'
    | 'isMobile'
    | 'countryCode'
>;

function FieldSelectListAndMap({
    fieldList,
    field,
    setField,
    selectedProduct,
    product,
    showGrowersFields,
    fieldRefs,
    mapRef,
    handleFieldClick,
    fieldsQuery,
    lastCampaigns,
    isMobile,
    countryCode,
}: FieldSelectListAndMapProps) {
    // On mobile the design opens a full-screen map picker modal instead of the desktop
    // side-by-side layout.
    const [mapModalOpen, setMapModalOpen] = useState(false);

    const list = (
        <Stack height='100%' width='100%' sx={{ maxHeight: '500px', overflowY: 'auto' }}>
            {!!selectedProduct && (
                <Stack direction='row' spacing={2} sx={{ py: 1 }} justifyContent='space-between'>
                    <Typography variant='caption' color='primary' fontWeight='bold' sx={{ pl: 8 }}>
                        {_('Lotes')}
                    </Typography>
                    <Typography variant='caption' color='primary' fontWeight='bold' sx={{ pr: 2 }}>
                        {_('Rendimiento estimado')} {product?.catalogName}
                    </Typography>
                </Stack>
            )}
            {fieldList.map((f, i) => (
                <div key={f.id} ref={(el) => (fieldRefs.current[f.id] = el)}>
                    <FieldItem
                        field={f}
                        fieldIsSelected={field?.id === f.id}
                        setSelectedField={setField}
                        dataTestId={`field-item-${i}`}
                        showGrowerLabel={showGrowersFields}
                        lastCampaign={lastCampaigns.get(f.id)}
                    />
                </div>
            ))}
        </Stack>
    );

    if (isMobile) {
        return (
            // A single wrapping element so this is one flex item to the parent — the parent
            // toggles `column-reverse` on mobile, which would otherwise silently push the "Ver en
            // mapa" button (rendered first below) past the whole field list if these were
            // returned as loose fragment children instead.
            <Stack width='100%' height='100%'>
                <Stack direction='row' justifyContent='flex-end' width='100%'>
                    <Button
                        variant='text'
                        color='primary'
                        size='small'
                        startIcon={<MapIcon />}
                        onClick={() => setMapModalOpen(true)}
                        sx={{ textDecoration: 'underline' }}
                    >
                        {_('Ver en mapa')}
                    </Button>
                </Stack>
                {list}
                <FieldSelectMapModal
                    open={mapModalOpen}
                    onClose={() => setMapModalOpen(false)}
                    fieldList={fieldList}
                    country={countryCode}
                    onSelect={handleFieldClick}
                />
            </Stack>
        );
    }

    return (
        <>
            {list}
            {fieldList.length && !fieldsQuery.isLoading ? (
                <Box height='100%' width='100%' sx={{ position: 'relative' }} minHeight='350px'>
                    <MapComponent
                        ref={mapRef}
                        fields={fieldList}
                        clusterMarkers
                        onFieldClick={handleFieldClick}
                        offline={false}
                        noGeocoder
                        simplifyPolygons
                        selectedField={field}
                    />
                </Box>
            ) : null}
        </>
    );
}

export default FieldSelectListAndMap;
